import { Injectable } from "@angular/core";
import { Resolve, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from "@angular/router";
import { Observable, forkJoin, of } from "rxjs";
import { map, catchError, switchMap } from "rxjs/operators";
import { AzureSearchService, AzureSearchRequest } from "../../../azure-search/azure-search";
import { BaseResolverService, AuthenticationService, ErrorsHandler } from "../../../core/core";
import { ToastrService } from "ngx-toastr";
import { NgProgress } from "ngx-progressbar";
import { FulfillmentlViewModel } from "../models/fulfillment-view.model";
import { RentalItemStateService } from "../../rentals/services/rental-item-state.service";
import { FulfillmentRequest } from "../models/fulfillment-request.model";
import { RentalItemStateSearchModel } from "../../rentals/models/rental-item-state-search.model";
import { FulfillmentRequestItem } from "../models/fulfillment-request-item.model";
import { TransportationRequest } from "../../../transportation/transportation";
import { RentalItemStageName, RentalItemState } from "../../rentals/models/rental-item-state.model";
import { User } from "oidc-client";
import { ActivityService } from "../../../activity/activity";
import { InventoryItem } from "../../../inventory/inventory";

@Injectable({
  providedIn: "root",
})
export class FulfillmentResolverService extends BaseResolverService implements Resolve<FulfillmentlViewModel> {
  private user: User;
  constructor(
    errorHandler: ErrorsHandler,
    toastrService: ToastrService,
    progress: NgProgress,
    private azureSearchService: AzureSearchService,
    private rentalItemStateService: RentalItemStateService,
    private authenticationService: AuthenticationService,
    private activityService: ActivityService,
    private router: Router,
  ) {
    super(errorHandler, toastrService, progress);
    this.user = this.authenticationService.getUserNonAsync();
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<FulfillmentlViewModel> | Observable<never> {
    this.startProgressBar();
    const fulfillmentId = route.paramMap.get("id");

    return forkJoin({
      fulfillmentRequest: this.azureSearchService.getOne<FulfillmentRequest>(fulfillmentId, "fulfillment-requests"),
    }).pipe(
      switchMap(responses => {
        // get the rental item states for each rental item
        const fulfillment = responses.fulfillmentRequest as FulfillmentRequest;
        if (fulfillment.accountId != this.user.profile.account_id) {
          this.finishProgressBar();
          this.router.navigate(["/manage/owner/fleet"]);
          return of(null);
        }
        const rentalItemStateSearch = new RentalItemStateSearchModel();
        rentalItemStateSearch.pageSize = 1000;
        rentalItemStateSearch.rentalRequestIds = [fulfillment.rentalRequestId];
        rentalItemStateSearch.partitionKey = fulfillment.renterAccountId;
        rentalItemStateSearch.ownerAccountId = this.user.profile.account_id;

        const inventoryItemIds = fulfillment.items.map(d => d.inventoryId);

        const machineSearch = new AzureSearchRequest();
        machineSearch.filter = `search.in(id, '${inventoryItemIds}')`;

        //ugly
        return forkJoin({
          fulfillment: of(fulfillment),
          rentalItemStates: this.rentalItemStateService.getAll(rentalItemStateSearch),
          activities: this.activityService.getAllActivitiesByRentalRequestId(fulfillment.rentalRequestId, "Owner"),
          inventoryItems: this.azureSearchService.search(machineSearch, "machines"),
        });
      }),
      map(responses => {
        const fulfillment = responses.fulfillment as FulfillmentRequest;
        const rentalItemStates = responses.rentalItemStates.results as RentalItemState[];
        const activities = responses.activities;
        const inventoryItems = responses.inventoryItems.value as InventoryItem[];

        fulfillment.items.forEach((fulfillmentItem: FulfillmentRequestItem) => {
          // find the corresponding rental item state for this rental item
          const rentalItemState = rentalItemStates.find(d => d.inventoryItem.id == fulfillmentItem.inventoryId);
          fulfillmentItem.rentalItemState = rentalItemState;
          fulfillmentItem.activities = activities.filter(d => d.inventoryItemId == fulfillmentItem.inventoryId);
          fulfillmentItem.statusData = this.rentalItemStateService.getRentalItemStatusData(fulfillmentItem, false);
          fulfillmentItem.inventoryItem = inventoryItems.find(d => d.id == fulfillmentItem.inventoryId);
        });

        this.finishProgressBar();
        return {
          fulfillment,
        } as FulfillmentlViewModel;
      }),
      catchError(e => {
        return this.logError(e);
      }),
    );
  }
}
